import React, { useState, useEffect, useRef } from "react";
import { Container, Row, Col, Card, Form, Button, ListGroup, InputGroup, Spinner, Badge } from "react-bootstrap";
import { Search, Send, MessageCircle, User } from "lucide-react";
import axios from "axios";
import { useUser } from "../../contextProviders/userContext";
import Sidebar from "../../components/SideBar";
import TopNavBar from "../../components/topNavBar";
import { useLocation } from "react-router-dom";
import { API_BASE_URL } from "../../api";

const DirectMessages = () => {
  const { user } = useUser();
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [isSending, setIsSending] = useState(false);

  const messagesEndRef = useRef(null);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Fetch conversations list
  const fetchConversations = async (selectUserId = null) => {
    try {
      setLoadingConvs(true);
      const res = await axios.get(`${API_BASE_URL}/messages/conversations`, {
        headers: getAuthHeader(),
      });
      setConversations(res.data || []);
      
      // If a selectUserId is provided, try to find and set it as active
      if (selectUserId) {
        const found = res.data.find(c => c.otherUser._id === selectUserId);
        if (found) {
          setActiveChat(found.otherUser);
        } else {
          // If not in active conversations yet, fetch user details to start a new chat
          try {
            const userRes = await axios.get(`${API_BASE_URL}/users?search=${selectUserId}`, {
              headers: getAuthHeader(),
            });
            const specificUser = userRes.data.find(u => u._id === selectUserId);
            if (specificUser) {
              setActiveChat(specificUser);
            }
          } catch (err) {
            console.error("Error fetching specific user details:", err);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching conversations:", error);
    } finally {
      setLoadingConvs(false);
    }
  };

  // Initial fetch
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const userIdFromUrl = queryParams.get("userId");
    fetchConversations(userIdFromUrl);
  }, [location.search]);

  // Fetch conversation messages
  const fetchMessages = async (otherUserId) => {
    if (!otherUserId) return;
    try {
      const res = await axios.get(`${API_BASE_URL}/messages/${otherUserId}`, {
        headers: getAuthHeader(),
      });
      setMessages(res.data || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  // Poll for messages in the active chat every 4 seconds
  useEffect(() => {
    if (!activeChat) return;

    fetchMessages(activeChat._id);
    const intervalId = setInterval(() => {
      fetchMessages(activeChat._id);
    }, 4000);

    return () => clearInterval(intervalId);
  }, [activeChat]);

  // Scroll to bottom of chat
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Handle User Search
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([]);
      return;
    }

    const delayDebounceFn = setTimeout(async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/users?search=${searchQuery}`, {
          headers: getAuthHeader(),
        });
        // Filter out self
        const filtered = (res.data || []).filter(u => u._id !== user?.id && u._id !== user?._id);
        setSearchResults(filtered);
      } catch (err) {
        console.error("Error searching users:", err);
      }
    }, 300);

    return () => clearTimeout(delayDebounceFn);
  }, [searchQuery]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!inputText.trim() || !activeChat || isSending) return;

    try {
      setIsSending(true);
      const res = await axios.post(
        `${API_BASE_URL}/messages`,
        {
          recipientId: activeChat._id,
          text: inputText.trim(),
        },
        { headers: getAuthHeader() }
      );

      setMessages((prev) => [...prev, res.data]);
      setInputText("");
      // Refresh conversations list to update last message/order
      fetchConversations();
    } catch (error) {
      console.error("Error sending message:", error);
    } finally {
      setIsSending(false);
    }
  };

  const startChatWithUser = (targetUser) => {
    setActiveChat(targetUser);
    setSearchQuery("");
    setSearchResults([]);
    
    // Add to conversations locally if not present
    const exists = conversations.some(c => c.otherUser._id === targetUser._id);
    if (!exists) {
      setConversations(prev => [
        {
          otherUser: targetUser,
          lastMessage: { text: "No messages yet", createdAt: new Date() },
          unreadCount: 0
        },
        ...prev
      ]);
    }
  };

  const cardStyle = {
    backgroundColor: "#fff",
    borderRadius: "16px",
    border: "1px solid #E5E7EB",
    boxShadow: "0px 8px 24px rgba(0, 0, 0, 0.05)",
    overflow: "hidden",
  };

  const activeUserStyle = (convPartner) => {
    const isActive = activeChat && activeChat._id === convPartner._id;
    return {
      cursor: "pointer",
      backgroundColor: isActive ? "#F3F4F6" : "transparent",
      transition: "background-color 0.2s ease",
      padding: "16px",
    };
  };

  return (
    <div className="d-flex flex-column flex-lg-row" style={{ backgroundColor: "#f9fafb", minHeight: "100vh" }}>
      <Sidebar />
      <Container fluid className="py-3 px-4 flex-grow-1" style={{ maxHeight: "100vh", overflowY: "auto" }}>
        <TopNavBar />

        <Row className="mt-4" style={{ height: "calc(100vh - 120px)" }}>
          {/* Left panel: Conversations List */}
          <Col lg={4} className="mb-4 mb-lg-0 h-100">
            <Card className="h-100" style={cardStyle}>
              <Card.Header className="bg-white border-0 pt-4 px-4 pb-2">
                <h5 className="fw-bold mb-3" style={{ color: "#006089" }}>Consultations</h5>
                {/* Search Bar */}
                <div className="position-relative">
                  <InputGroup style={{ borderRadius: "10px", overflow: "hidden" }}>
                    <InputGroup.Text className="bg-light border-0">
                      <Search size={18} className="text-muted" />
                    </InputGroup.Text>
                    <Form.Control
                      type="text"
                      placeholder={user?.role === "admin" ? "Search users..." : "Search admins or project leaders..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="bg-light border-0 py-2.5"
                    />
                  </InputGroup>

                  {/* Search Results Dropdown */}
                  {searchResults.length > 0 && (
                    <Card className="position-absolute w-100 shadow-lg mt-1" style={{ zIndex: 1000, maxHeight: "250px", overflowY: "auto" }}>
                      <ListGroup variant="flush">
                        {searchResults.map((su) => (
                          <ListGroup.Item
                            key={su._id}
                            action
                            onClick={() => startChatWithUser(su)}
                            className="d-flex align-items-center gap-3 py-3"
                          >
                            <div className="rounded-circle bg-light d-flex align-items-center justify-content-center" style={{ width: "40px", height: "40px" }}>
                              <User size={18} className="text-secondary" />
                            </div>
                            <div className="text-start">
                              <div className="fw-bold">{su.organizationName || `${su.name} ${su.surname || ""}`.trim()}</div>
                              <small className="text-muted">
                                {su.role === "admin" ? "Administrator" : su.role === "organization" ? "Project organisation" : su.faculty || "Project leader"}
                              </small>
                            </div>
                          </ListGroup.Item>
                        ))}
                      </ListGroup>
                    </Card>
                  )}
                </div>
              </Card.Header>

              {/* Conversations Scroller */}
              <Card.Body className="p-0 overflow-auto" style={{ height: "calc(100% - 130px)" }}>
                {loadingConvs ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <Spinner animation="border" variant="primary" />
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted p-4">
                    <MessageCircle size={48} className="mb-2 opacity-50" />
                    <p className="small mb-0">No active consultations yet.</p>
                    <p className="small text-center mt-1">Use the search bar above to start one.</p>
                  </div>
                ) : (
                  <ListGroup variant="flush">
                    {conversations.map((conv) => (
                      <ListGroup.Item
                        key={conv.otherUser._id}
                        style={activeUserStyle(conv.otherUser)}
                        onClick={() => setActiveChat(conv.otherUser)}
                        className="border-0 border-bottom"
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          <div className="d-flex align-items-center gap-3">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white shadow-sm"
                              style={{
                                width: "45px",
                                height: "45px",
                                backgroundColor: conv.otherUser.role === "admin" ? "#006089" : "#0DADB2",
                              }}
                            >
                              {conv.otherUser.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="text-start">
                              <h6 className="fw-bold mb-1 text-dark">
                                {conv.otherUser.organizationName || `${conv.otherUser.name} ${conv.otherUser.surname || ""}`.trim()}
                              </h6>
                              <div className="text-muted small text-truncate" style={{ maxWidth: "160px" }}>
                                {conv.lastMessage?.text || "No messages yet"}
                              </div>
                            </div>
                          </div>
                          <div className="text-end">
                            {conv.unreadCount > 0 && (
                              <Badge bg="danger" pill className="px-2 py-1 small">
                                {conv.unreadCount}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Card.Body>
            </Card>
          </Col>

          {/* Right panel: Messages Thread */}
          <Col lg={8} className="h-100">
            <Card className="h-100" style={cardStyle}>
              {activeChat ? (
                <>
                  {/* Chat Partner Header */}
                  <Card.Header className="bg-white border-bottom py-3 px-4 d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white shadow-sm"
                      style={{
                        width: "45px",
                        height: "45px",
                        backgroundColor: activeChat.role === "admin" ? "#006089" : "#0DADB2",
                      }}
                    >
                      {activeChat.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="text-start">
                      <h6 className="fw-bold mb-0 text-dark">
                        {activeChat.organizationName || `${activeChat.name} ${activeChat.surname || ""}`.trim()}
                      </h6>
                      <small className="text-muted">
                        {activeChat.role === "admin" ? "WCCO Administrator" : activeChat.role === "organization" ? "Project organisation" : `Student • ${activeChat.faculty || "Wits University"}`}
                      </small>
                    </div>
                  </Card.Header>

                  {/* Messages Bubble Container */}
                  <Card.Body className="bg-light p-4 overflow-auto" style={{ height: "calc(100% - 150px)" }}>
                    <div className="d-flex flex-column gap-3">
                      {messages.length === 0 ? (
                        <div className="text-center py-5 text-muted small">
                          This is the beginning of your consultation with {activeChat.name}.
                        </div>
                      ) : (
                        messages.map((msg) => {
                          const isSelf = msg.sender._id === user?.id || msg.sender._id === user?._id || msg.sender === user?.id || msg.sender === user?._id;
                          return (
                            <div
                              key={msg._id}
                              className={`d-flex ${isSelf ? "justify-content-end" : "justify-content-start"}`}
                            >
                              <div
                                style={{
                                  maxWidth: "70%",
                                  borderRadius: "16px",
                                  padding: "12px 16px",
                                  backgroundColor: isSelf ? "#006089" : "#fff",
                                  color: isSelf ? "#fff" : "#1F2937",
                                  boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
                                  textAlign: "left"
                                }}
                              >
                                <div style={{ fontSize: "0.95rem", whiteSpace: "pre-wrap" }}>{msg.text}</div>
                                <div
                                  className={`text-end mt-1 ${isSelf ? "text-white-50" : "text-muted"}`}
                                  style={{ fontSize: "0.7rem" }}
                                >
                                  {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                      <div ref={messagesEndRef} />
                    </div>
                  </Card.Body>

                  {/* Messages Input Box */}
                  <Card.Footer className="bg-white border-0 p-3">
                    <Form onSubmit={handleSendMessage} className="d-flex gap-2">
                      <Form.Control
                        type="text"
                        placeholder="Type your consultation message here..."
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        disabled={isSending}
                        style={{ borderRadius: "10px", padding: "12px" }}
                      />
                      <Button
                        type="submit"
                        disabled={!inputText.trim() || isSending}
                        style={{
                          backgroundColor: "#006089",
                          border: "none",
                          borderRadius: "10px",
                          width: "50px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Send size={18} color="#fff" />
                      </Button>
                    </Form>
                  </Card.Footer>
                </>
              ) : (
                <div className="d-flex flex-column align-items-center justify-content-center h-100 text-muted p-5">
                  <MessageCircle size={60} className="mb-3 opacity-30" style={{ color: "#006089" }} />
                  <h5 className="fw-bold">Your Direct Consultations</h5>
                  <p className="small text-center" style={{ maxWidth: "320px" }}>
                    Select an active conversation from the sidebar list, or search for a user to start a consultation.
                  </p>
                </div>
              )}
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default DirectMessages;
